import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import dynamic from "next/dynamic";
import type { AppProps, NextWebVitalsMetric } from "next/app";
import { AppContext } from "next/app";
import { ThemeProvider } from "styled-components";
import GlobalStyle from "global/globalStyle";
import { CookiesConstants } from "utils/constants";
import { useRouter } from "next/router";
import { IMember } from "../screens/AccountMembership/_private/types/midView";
import theme from "global/theme";
import type { Page } from "types/page";
import enhanceContext from "../private/enhanceContext";
import AppContextProvider from "contexts/AppContextProvider";
import { IMegaMenu } from "models/megaMenu";
import Head from "next/head";
import { ICategoryAsset } from "models/Assets/ICategoryAsset";
import AgentContextProvider from "contexts/UserAgentContext";
import { isBrowser } from "utils/nookies/utils";
import { AlertCard } from "../componentReusable/UIComponents/StickyAlertMsg/styles.styled";
import { ToastContainer } from "react-toastify";
import { getLang } from "helpers/themeHelpers";
import { useTriggerShareByUrl } from "../hooks/useTriggerShareByUrl";
import FirebaseFCM from "../componentReusable/FirebaseFCM";
import { useLogout } from "../hooks/useLogout";
import { useLogoutCallback } from "../hooks/useLogoutCallback";
import useFixWebp from "../hooks/useFixWebp";
import useClickStreamSource from "../hooks/useClickStream";
import { useAuthSSO } from "../hooks/useAuthSSO";
import useClickEventListenerLogging from "../libs/logging/hooks/useClickEventListenerLogging";
import AuthSsoProvider from "contexts/authSSOContext";
import MainChatProvider from "contexts/ChatContextProvider";
import useLocationPermission from "../hooks/useLocationPermission";
import { destroyCookie } from "utils/nookies";

export function reportWebVitals(metric: NextWebVitalsMetric) {
  console.info(metric);
}

const ErrorPage = dynamic(() => import("componentScreens/ErrorPage"), {
  suspense: false,
});

declare global {
  interface Window {
    google: any;
  }
}

function onError(errO) {
  if (typeof document === "undefined") {
    return;
  }

  //client only
  // @ts-ignore
  window.err = {
    error: {
      message: errO?.message ? errO?.message : "error tracker",
      name: errO?.name,
      stack: (errO?.stack).split("\n").slice(0, 3) || [],
    },
    cookies: document.cookie,
    userAgent: navigator.userAgent,
    location: window.location.href,
    source: document.cookie.match(/; source=([^;]*)/)?.[1] || "unknown",
    country: document.cookie.match(/ecountry=([^;]*)/)?.[1] || "unknown",
    lang: document.cookie.match(/NEXT_LOCALE=([^;]*)/)?.[1] || "unknown",
  };
  fetch(`/error-tracker-csr`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    // @ts-ignore
    body: JSON.stringify(window?.err || {}),
  })
    .then((res) => res.text())
    .then(console.log);
}

if (typeof document !== "undefined") {
  window.onerror = (message, source, lineno, colno, error) => onError(error);
}

interface Props extends AppProps {
  userAgent: string;
  requestUri: string;
  requestUriNoQs: string;
  cityId: number;
  isChangeCountryPage: boolean;
  showTopBanner: boolean;
  enable3rdParty: boolean;
  enableViber: boolean;
  seoPage;
  translations?: any;
  host: string;
  country: string;
  protocol: typeof theme.protocol;
  domain: string;
  isMobile: boolean;
  isDesktop: boolean;
  device: string;
  isSplashScreenHiding: boolean;
  memberData: IMember;
  megaMenu: IMegaMenu;
  locale: string;
  otpData: { otp_Code: string; phone_Number: string };
  hostNoSubDomain: string;
  isUserLocationCountryAllowed: boolean;
  categories: ICategoryAsset[];
  url: string;
  ipCountryCode: string;
}

type AppPropsWithLayout = Props & {
  Component: Page;
};

function CustomErrorPage() {
  if (!isBrowser()) return <>CSR ERROR on Server</>;
  const errorToLog = {
    is_ssr: false,
    is_express: true,
    userAgent: navigator.userAgent,
    location: document.location.href,
    cookies: document.cookie,
    // @ts-ignore
    error: window.err,
    country:
      (document.cookie || "").match(/ecountry=([^;]*)/)?.[1] || "unknown",
    source: (document.cookie || "").match(/; source=([^;]*)/)?.[1] || "unknown",
    lang:
      (document.cookie || "").match(/NEXT_LOCALE=([^;]*)/)?.[1] || "unknown",
  };
  return (
    <>
      <ErrorPage />
      {process.env.PROFILE_ENV !== "production" && (
        <pre>{JSON.stringify(errorToLog, null, 2)}</pre>
      )}
    </>
  );
}

function MyApp({
  userAgent,
  requestUri,
  requestUriNoQs,
  cityId,
  isChangeCountryPage,
  showTopBanner,
  enable3rdParty,
  enableViber,
  translations,
  Component,
  pageProps,
  host,
  country,
  protocol,
  domain,
  isMobile,
  isDesktop,
  device,
  isSplashScreenHiding,
  memberData: memberDataDefault,
  locale,
  megaMenu,
  hostNoSubDomain,
  isUserLocationCountryAllowed,
  categories,
  url,
  ipCountryCode,
}: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const router = useRouter();
  const getLayout = Component.getLayout ?? ((page) => page);
  const [currentTab, setCurrentTab] = useState("");
  const [redirectOnClose, setRedirectOnClose] = useState<boolean>(true);
  const { auth, loggedInState, userInfo } = useAuthSSO(
    device,
    locale,
    country,
    hostNoSubDomain,
    cityId,
    memberDataDefault
  );
  const [memberData, setMemberData] = useState<IMember>(memberDataDefault);

  useEffect(() => {
    setMemberData(userInfo);
  }, [userInfo]);

  useTriggerShareByUrl();

  theme.direction = locale === "ar" ? "rtl" : "ltr";
  // @ts-ignore
  theme.translations = {
    ...(translations || {}),
    // @ts-ignore
    ...(pageProps?.translations || {}),
  };
  theme.isLTR = locale === "en";
  theme.isRTL = locale === "ar";
  theme.locale = locale === "en" ? "en" : "ar";
  theme.url = url;
  theme.isRegistered = memberData?.hasPassword;
  theme.ipCountryCode = ipCountryCode;

  useEffect(() => {
    document.body.classList.remove("ssr");
  }, []);

  useLogout(memberData);
  useLocationPermission();

  useLogoutCallback(() => {
    destroyCookie(null, CookiesConstants.LAST_CONTACTED);
    if (!window.location.href.includes("?source=logout")) {
      window.location.reload();
    }
  });

  useFixWebp();
  useClickStreamSource();
  useClickEventListenerLogging();
  useLogout(memberData);

  const updateCurrentTab = (tab: string) => {
    setCurrentTab(tab);
  };

  useEffect(() => {
    if (
      window.location.href.includes("/listing/create") &&
      document.querySelector("#addPost_btn")
    ) {
      document.getElementById("addPost_btn").click();
      history.pushState({}, "", `/${getLang()}`);
    }
  }, []);

  const object404 = url == "/404" ? { categories } : {};
  return (
    <ThemeProvider
      theme={{
        ...theme,
        megaMenu,
        memberData,
        host,
        hostNoSubDomain,
        isUserLocationCountryAllowed,
        country,
        cityId,
        currentTab,
        protocol,
        requestUri,
        requestUriNoQs,
        domain,
        isMobile,
        isDesktop,
        device,
        isSplashScreenHiding,
        // LoginMember,
        updateCurrentTab,
        // redirectOnLogin,
        redirectOnClose,
        isChangeCountryPage,
        showTopBanner,
        enable3rdParty,
        enableViber,
      }}
    >
      <AlertCard>
        <ToastContainer
          autoClose={false}
          draggableDirection="y"
          draggablePercent={60}
        />
      </AlertCard>
      <AgentContextProvider userAgent={userAgent}>
        <ErrorBoundary
          key={router.asPath}
          onError={onError}
          FallbackComponent={CustomErrorPage}
          onReset={() => {
            // reset the state of your app so the error doesn't happen again
          }}
        >
          <Head>
            <meta
              name="viewport"
              key="viewport"
              content="width=device-width, height=device-height ,initial-scale=1.0, shrink-to-fit=no"
            />
          </Head>
          <GlobalStyle />
          <FirebaseFCM />
          <AuthSsoProvider
            auth={auth}
            loggedInState={loggedInState}
            userInfo={userInfo}
          >
            <MainChatProvider>
              <AppContextProvider {...pageProps}>
                {getLayout(
                  <Component
                    key={router.asPath}
                    {...pageProps}
                    {...object404}
                  />,
                  pageProps
                )}
              </AppContextProvider>
            </MainChatProvider>
          </AuthSsoProvider>
        </ErrorBoundary>
      </AgentContextProvider>
    </ThemeProvider>
  );
}

MyApp.getInitialProps = async ({ ctx }: AppContext) => {
  console.time("getInitialProps");
  const context = await enhanceContext(ctx);
  const { cookies } = context;
  const megaMenu = context.essentialData?.getMegaMenu();
  const is404Path = context.pathname == "/404";
  const isErrorPath = context.pathname == "/_error";
  if (isErrorPath && context?.err) {
    const err = {
      is_ssr: true,
      message:
        context?.err?.message ||
        JSON.stringify(context?.err || "error-not-known"),
      userAgent: context?.userAgent,
      location: context?.originalUrl,
      cookies: context?.cookies,
      country: context?.cookies["ecountry"] || "unknown",
      lang: context?.cookies["NEXT_LOCALE"] || "unknown",
      error: {
        name: context?.err?.name,
        message: context?.err?.message,
        stack: context?.err?.stack,
      },
      userIP: context?.userIP,
    };
    context?.logger?.error && context.logger.error(err);
  }

  const catsObj = is404Path
    ? { categories: context?.essentialData?.getCategories_ServerOnly?.() || [] }
    : {};

  console.timeEnd("getInitialProps");
  return {
    megaMenu: context.getData("source") === "desktop" ? megaMenu : "",
    userAgent: context.userAgent,
    requestUri: context.requestUri,
    requestUriNoQs: context.requestUri.split("?")[0],
    userIp: context.userIP,
    seoPage: context.seoPage,
    translations: context.essentialData?.getCommonTranslation() || {},
    showTopBanner: !cookies[CookiesConstants.HIDE_DOWNLOAD_BANNER_COUNTER],
    enable3rdParty:
      !cookies[CookiesConstants.REMOVE_3RD_PARTIES] &&
      !context.query.remove3rdParties,
    locale: context.lang,
    isChangeCountryPage: context.isChangeCountryPage,
    host: context.host,
    hostNoSubDomain: context.hostNoSubDomain,
    isUserLocationCountryAllowed: context.isUserLocationCountryAllowed,
    country: context.countryCode,
    protocol: context.protocol,
    domain: context.host,
    isMobile: context.getData("source") === "mobile",
    isDesktop: context.getData("source") === "desktop",
    device: context.getData("source"),
    isSplashScreenHiding: Boolean(
      cookies[CookiesConstants.SPLASH_SCREEN_FOR_ONE_TIME_ONLY]
    ),
    cityId: Number(cookies[CookiesConstants.CITY_ID] || null),
    memberData: context.memberData,
    // enableViber: showViber(context.countryCode),
    url: context.pathname,
    ...catsObj,
    ipCountryCode: context.ipCountryCode,
  };
};

export default MyApp;
